// errorBoundary.tsx
import React, { Component } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import StaticLoading from 'src/components/common/loading';
import { sendMonitorEvent, xflowPushEvent } from 'src/utils/pageTrack';
import { Button } from 'zarm';
import './ErrorBoundary.scss';

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  info?: React.ErrorInfo;
  prevPathname?: string;
}

interface ErrorBoundaryProps extends RouteComponentProps {
  children: React.ReactNode;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      prevPathname: props.location.pathname,
    };
  }

  static getDerivedStateFromError(error: Error) {
    // 更新状态以显示错误界面
    return { hasError: true, error };
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    // 监听路由变化，如果路由改变了则重置错误状态
    if (this.props.location.pathname !== prevProps.location.pathname) {
      if (this.state.hasError) {
        this.setState({ 
          hasError: false, 
          error: undefined,
          prevPathname: this.props.location.pathname,
        });
      }
    }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    try {
      // 捕获组件树中的错误并发送监控事件
      sendMonitorEvent(
        'REACT_COMPONENT_ERROR',
        'error',
        'React组件错误',
        {
          componentName: errorInfo.componentStack?.split('\n')[0] || 'UnknownComponent',
          message: error.message,
          stack: error.stack,
          componentStack: errorInfo.componentStack,
          pathname: this.props.location.pathname,
        }
      );
    } catch (e) {
      console.error('Error logging failed', e);
    }
  }

  handleReload = () => {
    // 埋点：刷新页面按钮点击
    xflowPushEvent({
      event: 'click',
      eventTag: 'error_boundary_reload',
      text: '页面白屏-刷新页面',
    });
    StaticLoading.show();
    window.location.reload();
  };

  render() {
    // 如果捕获到错误，显示降级UI，防止错误组件继续渲染导致无限循环
    if (this.state.hasError) {
      const { error } = this.state;
      const isDev = process.env.NODE_ENV === 'development';

      return (
        <div className='error-boundary-container'>
          <div className='error-content'>
            <div className='error-icon'>
              <img src={require('src/images/error.svg')} alt='错误' />
            </div>
            
            <h2 className='error-title'>抱歉，页面出现了一些问题</h2>
            
            {isDev && error?.message && (
              <div className='error-message'>
                {error.message}
              </div>
            )}
            
            <p className='error-tips'>
              您可以尝试刷新页面
            </p>

            <div className='error-actions'>
              <Button className='btn-primary' theme='primary' shape='round' onClick={this.handleReload}>
                刷新页面
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default withRouter(ErrorBoundary);
